import { type SupabaseClient } from '@supabase/supabase-js'
import z from 'zod'
import { type FeedbackRowWithAsset } from '../contacts/types.ts'
import { type TablesInsert, type Database, type Tables } from '../db/supabase.types.ts'
import { generateShortId } from '../lib/shortId.ts'
import { userProfileDataSchema } from '../user/types.ts'
import { getFeedbackRequestEligibility } from './eligibility.ts'
import { type SendScheduleKey, sendScheduleKeys, sendScheduleOptions } from './schedule.ts'
import { type FeedbackSendingMethod, feedbackSendingMethodSchema } from './types.ts'

export const DEFAULT_MESSAGE_FI =
  'Hei, kiitos käynnistä! Olisimme kiitollisia, jos voisit vastata lyhyeen kyselyyn kohteesta.'

export const createFeedbackRequestSchema = z
  .object({
    userId: z.string(),
    contactIds: z.array(z.string()).min(1).max(10),
    sendAt: sendScheduleKeys.optional(),
    sendMethod: feedbackSendingMethodSchema.optional(),
  })
  .transform((data) => {
    if (data.sendMethod === 'email' && data.sendAt && data.sendAt !== 'now') {
      return { ...data, sendAt: 'now' as const }
    }

    return data
  })

export const FEEDBACK_REQUEST_ERRORS = {
  CONTACT_NOT_FOUND: 'CONTACT_NOT_FOUND',
  CONTACT_DISABLED: 'CONTACT_DISABLED',
  ASSET_NOT_FOUND: 'ASSET_NOT_FOUND',
  USER_PROFILE_NOT_FOUND: 'USER_PROFILE_NOT_FOUND',
  FEEDBACK_REQUEST_MINIMUM_TIMEOUT: 'FEEDBACK_REQUEST_MINIMUM_TIMEOUT',
  FEEDBACK_RECEIVED: 'FEEDBACK_RECEIVED',
  CONTACT_NO_SENDING_CHANNEL: 'CONTACT_NO_SENDING_CHANNEL',
  CONTACT_NO_EMAIL_FOR_METHOD: 'CONTACT_NO_EMAIL_FOR_METHOD',
  CONTACT_NO_PHONE_FOR_METHOD: 'CONTACT_NO_PHONE_FOR_METHOD',
  FEEDBACK_ALREADY_SENT: 'FEEDBACK_ALREADY_SENT',
  FEEDBACK_ALREADY_ANSWERED: 'FEEDBACK_ALREADY_ANSWERED',
  SEND_EMAIL_FAILED: 'SEND_EMAIL_FAILED',
  SEND_SMS_FAILED: 'SEND_SMS_FAILED',
  SEND_ALL_METHODS_FAILED: 'SEND_ALL_METHODS_FAILED',
} as const

export type CreateFeedbackRequestError =
  (typeof FEEDBACK_REQUEST_ERRORS)[keyof typeof FEEDBACK_REQUEST_ERRORS]

export type AddFeedbackRequestsResult = {
  inserted: Tables<'feedback'>[]
  errors: {
    assetId: string | null
    error: (typeof FEEDBACK_REQUEST_ERRORS)[keyof typeof FEEDBACK_REQUEST_ERRORS]
    nextSendAt?: string
  }[]
}

function isPostgresUniqueViolation(error: unknown) {
  if (!error || typeof error !== 'object') {
    return false
  }

  const maybeError = error as { code?: unknown }

  return maybeError.code === '23505'
}

function resolveFeedbackMethodForContact(
  preferredSendMethod: FeedbackSendingMethod,
  contactData: { email: string | null; phone: string | null }
):
  | { method: FeedbackSendingMethod }
  | { error: (typeof FEEDBACK_REQUEST_ERRORS)[keyof typeof FEEDBACK_REQUEST_ERRORS] } {
  if (!contactData.email && !contactData.phone) {
    return { error: FEEDBACK_REQUEST_ERRORS.CONTACT_NO_SENDING_CHANNEL }
  }

  if (preferredSendMethod === 'sms') {
    if (contactData.phone) {
      return { method: 'sms' }
    }

    if (contactData.email) {
      return { method: 'email' }
    }

    return { error: FEEDBACK_REQUEST_ERRORS.CONTACT_NO_PHONE_FOR_METHOD }
  }

  if (contactData.email) {
    return { method: 'email' }
  }

  if (contactData.phone) {
    return { method: 'sms' }
  }

  return { error: FEEDBACK_REQUEST_ERRORS.CONTACT_NO_EMAIL_FOR_METHOD }
}

type AddFeedbackRequestsInput = {
  userId: string
  contactId: string
  hasRealtorContactMethod: boolean
  sendAt?: SendScheduleKey
  sendMethod?: FeedbackSendingMethod
  followUpEnabled?: boolean
}

export async function addFeedbackRequests(
  {
    userId,
    contactId,
    hasRealtorContactMethod,
    sendAt,
    sendMethod,
    followUpEnabled = true,
  }: AddFeedbackRequestsInput,
  supabase: SupabaseClient<Database>
): Promise<AddFeedbackRequestsResult> {
  const [profileRes, contactRes] = await Promise.all([
    supabase.from('user_profile').select('data, name').eq('user_id', userId).maybeSingle(),
    supabase
      .from('contacts')
      .select('id, email, phone, disabled_at, disabled_reason')
      .eq('id', contactId)
      .is('deleted_at', null)
      .maybeSingle(),
  ])

  if (contactRes.error) {
    throw contactRes.error
  }

  if (!contactRes.data) {
    throw new Error(FEEDBACK_REQUEST_ERRORS.CONTACT_NOT_FOUND)
  }

  const contactData = contactRes.data

  if (contactData.disabled_at !== null && contactData.disabled_reason !== 'RESERVED') {
    throw new Error(FEEDBACK_REQUEST_ERRORS.CONTACT_DISABLED)
  }

  if (profileRes.error) {
    throw profileRes.error
  }

  const profileData = userProfileDataSchema.safeParse(profileRes?.data?.data)?.data

  const isProfileComplete =
    !!profileRes.data?.name && !!profileData?.avatar_url && hasRealtorContactMethod

  if (!isProfileComplete || !profileData) {
    console.error('Profile data not found for user', userId)
    throw new Error(FEEDBACK_REQUEST_ERRORS.USER_PROFILE_NOT_FOUND)
  }

  const refDate = new Date()
  const timing: SendScheduleKey = sendAt ?? 'now'

  const preferredSendMethod: FeedbackSendingMethod = sendMethod ?? 'email'
  const resolvedMethod = resolveFeedbackMethodForContact(preferredSendMethod, contactData)

  if ('error' in resolvedMethod) {
    return {
      inserted: [],
      errors: [
        {
          assetId: null,
          error: resolvedMethod.error,
        },
      ],
    }
  }

  const finalTiming: SendScheduleKey = resolvedMethod.method === 'email' ? 'now' : timing
  const schedule = sendScheduleOptions.find((option) => option.key === finalTiming)
  const sendAtDate = schedule?.value(refDate) ?? refDate

  const baseRowPayload = {
    user_id: userId,
    contact_id: contactId,
    sending_method: resolvedMethod.method,
    follow_up_enabled: followUpEnabled,
    send_at: sendAtDate.toISOString(),
    short_id: generateShortId(),
  } satisfies TablesInsert<'feedback'>

  const { data: requestedGeneralFeedback, error: requestedGeneralFeedbackError } = await supabase
    .from('feedback')
    .select('created_at, completed_at, declined_at')
    .eq('contact_id', contactId)
    .is('deleted_at', null)

  if (requestedGeneralFeedbackError) {
    throw requestedGeneralFeedbackError
  }

  const generalRows: FeedbackRowWithAsset[] = (requestedGeneralFeedback ??
    []) as FeedbackRowWithAsset[]

  const generalEligibility = getFeedbackRequestEligibility({
    feedbackRows: generalRows,
    now: refDate,
  })

  if (!generalEligibility.canSend) {
    if (generalEligibility.reason === 'contactDisabled') {
      throw new Error(FEEDBACK_REQUEST_ERRORS.CONTACT_DISABLED)
    }

    if (generalEligibility.reason === 'cooldown') {
      return {
        inserted: [],
        errors: [
          {
            assetId: null,
            error: FEEDBACK_REQUEST_ERRORS.FEEDBACK_REQUEST_MINIMUM_TIMEOUT,
            nextSendAt: generalEligibility.nextAllowedAt?.toISOString(),
          },
        ],
      }
    }

    return {
      inserted: [],
      errors: [{ assetId: null, error: FEEDBACK_REQUEST_ERRORS.FEEDBACK_ALREADY_SENT }],
    }
  }

  const { data: insertedGeneralFeedback, error: insertedGeneralFeedbackError } = await supabase
    .from('feedback')
    .insert(baseRowPayload)
    .select()

  if (insertedGeneralFeedbackError) {
    if (isPostgresUniqueViolation(insertedGeneralFeedbackError)) {
      return {
        inserted: [],
        errors: [{ assetId: null, error: FEEDBACK_REQUEST_ERRORS.FEEDBACK_ALREADY_SENT }],
      }
    }

    throw insertedGeneralFeedbackError
  }

  return { inserted: insertedGeneralFeedback ?? [], errors: [] }
}

export type UpdateFeedbackRequestInput = {
  feedbackId: string
  sendAt?: Date | null
  sendingMethod?: z.infer<typeof feedbackSendingMethodSchema>
}

export const FEEDBACK_UPDATE_ERRORS = {
  FEEDBACK_NOT_FOUND: 'FEEDBACK_NOT_FOUND',
  UPDATE_NOT_ALLOWED: 'UPDATE_NOT_ALLOWED',
  NO_FIELDS_TO_UPDATE: 'NO_FIELDS_TO_UPDATE',
} as const

export type UpdateFeedbackRequestError =
  (typeof FEEDBACK_UPDATE_ERRORS)[keyof typeof FEEDBACK_UPDATE_ERRORS]

export async function updateFeedbackRequest(
  { feedbackId, sendAt, sendingMethod }: UpdateFeedbackRequestInput,
  supabase: SupabaseClient<Database>
): Promise<void> {
  if (typeof sendingMethod === 'undefined' && typeof sendAt === 'undefined') {
    throw new Error(FEEDBACK_UPDATE_ERRORS.NO_FIELDS_TO_UPDATE)
  }

  const [existingRes, sentNotificationRes] = await Promise.all([
    supabase
      .from('feedback')
      .select('id, completed_at, responder_user_id')
      .eq('id', feedbackId)
      .maybeSingle(),
    supabase
      .from('feedback_notifications')
      .select('id')
      .eq('feedback_id', feedbackId)
      .eq('status', 'sent')
      .limit(1)
      .maybeSingle(),
  ])

  if (existingRes.error) {
    throw existingRes.error
  }

  if (!existingRes.data) {
    throw new Error(FEEDBACK_UPDATE_ERRORS.FEEDBACK_NOT_FOUND)
  }

  const existing = existingRes.data
  const hasBeenSent = !!sentNotificationRes.data
  const hasCompleted = !!existing.completed_at
  const hasResponder = !!existing.responder_user_id

  if (hasBeenSent || hasCompleted || hasResponder) {
    throw new Error(FEEDBACK_UPDATE_ERRORS.UPDATE_NOT_ALLOWED)
  }

  const payload: {
    sending_method?: z.infer<typeof feedbackSendingMethodSchema>
    send_at?: string | null
  } = {}

  if (typeof sendingMethod !== 'undefined') {
    payload.sending_method = sendingMethod
  }

  if (typeof sendAt !== 'undefined') {
    payload.send_at = sendAt ? sendAt.toISOString() : null
  }

  if (!Object.keys(payload).length) {
    throw new Error(FEEDBACK_UPDATE_ERRORS.NO_FIELDS_TO_UPDATE)
  }

  const { error } = await supabase.from('feedback').update(payload).eq('id', feedbackId)

  if (error) {
    throw error
  }
}
